import { TrialEndModal } from '@/components/billing/trial-end-modal';
import { Icons } from '@/components/icon';
import { suppressPersonalPhoneModal } from '@/components/personal-phone-modal/personal-phone-modal-suppression';
import { SettingsSectionHeader } from '@/components/settings/section-header';
import { Team } from '@/components/team-management/team/team';
import { TeamBusinessInviteDialog } from '@/components/team-management/team/team-business-invite-dialog';
import { TeamBusinessUpgradeDialog } from '@/components/team-management/team/team-business-upgrade-dialog';
import { Button, Layout } from '@/components/ui/design-system';
import {
  TRIAL_TEAM_LIMIT,
  useIsAdminOrManager,
  useIsTeamAdmin,
  useTeamSubscriptionData,
} from '@/hooks/api/team';
import { useTranslation } from '@/hooks/use-translation';
import amplitudeService from '@/services/amplitude';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/_app/settings/team')({
  component: RouteComponent,
  validateSearch: z.object({
    openModal: z.boolean().optional(),
    editMemberId: z.string().optional(),
    prefillName: z.string().optional(),
    prefillEmail: z.string().email().optional(),
  }),
});

function RouteComponent() {
  const { t } = useTranslation('desktop_ui');
  const { isTeamAdmin } = useIsTeamAdmin();
  const { isAdminOrManager } = useIsAdminOrManager();
  const {
    isLoading: isSubscriptionLoading,
    isBusiness,
    isTrial,
    memberCount,
  } = useTeamSubscriptionData();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isTrialEndOpen, setIsTrialEndOpen] = useState(false);
  const [inviteSource, setInviteSource] = useState<'settings' | 'workspace_modal'>('settings');
  // Captured from URL search params (deep-link from the join-request owner
  // notification email) before we strip them, so the dialog still sees them
  // after the URL is replaced.
  const [prefillName, setPrefillName] = useState<string | undefined>();
  const [prefillEmail, setPrefillEmail] = useState<string | undefined>();
  // Bumped on every dialog open so the dialog remounts with fresh
  // defaultValues — avoids a useEffect+form.reset workaround in the dialog.
  const [inviteSessionId, setInviteSessionId] = useState(0);
  const navigate = useNavigate();
  const search = Route.useSearch();

  const shouldGateForTrial = isBusiness && isTrial && memberCount >= TRIAL_TEAM_LIMIT;

  const openInviteDialog = () => {
    setInviteSessionId((n) => n + 1);
    setIsInviteOpen(true);
  };

  const openInviteFlow = (source: 'settings' | 'workspace_modal') => {
    setInviteSource(source);
    if (shouldGateForTrial) {
      setIsTrialEndOpen(true);
    } else {
      openInviteDialog();
    }
  };

  useEffect(() => {
    // Wait until the subscription query resolves so the JSX branches to the
    // correct dialog (TeamBusinessInviteDialog vs TeamBusinessUpgradeDialog).
    // Otherwise isBusiness defaults to false and the deep link briefly opens
    // the paywall instead of the invite dialog.
    if (isSubscriptionLoading || search.openModal === undefined) {
      return;
    }
    // Suppress the ARCEP personal-phone modal so it doesn't stack on top of
    // the invite dialog — Radix mis-routes outside clicks across stacked
    // dialogs. The invite intent owns this entry point.
    suppressPersonalPhoneModal();
    amplitudeService.logEvent('Invite member Button Tapped', {
      isAdmin: isAdminOrManager,
      source: 'workspace_modal',
    });
    setInviteSource('workspace_modal');
    setPrefillName(search.prefillName);
    setPrefillEmail(search.prefillEmail);
    if (shouldGateForTrial) {
      setIsTrialEndOpen(true);
    } else {
      openInviteDialog();
    }
    const { openModal, prefillName: _, prefillEmail: __, ...otherSearchParams } = search;
    navigate({
      to: '.',
      search: otherSearchParams,
      replace: true,
    });
  }, [search, navigate, isAdminOrManager, shouldGateForTrial, isSubscriptionLoading]);

  const inviteButton =
    isTeamAdmin && !isSubscriptionLoading ? (
      <Button
        variant="outline"
        size="sm"
        iconName={Icons.Plus}
        onClick={() => {
          amplitudeService.logEvent('Invite member Button Tapped', {
            isAdmin: isAdminOrManager,
            source: 'settings',
          });
          openInviteFlow('settings');
        }}
      >
        {t('team.invite.button')}
      </Button>
    ) : undefined;

  return (
    <>
      <Layout isScrollable contentClassName="max-w-[960px] px-20 !pt-0 pb-30">
        <section className="flex flex-col gap-4">
          <SettingsSectionHeader
            title={t('settings.sidebar.members')}
            description={t('team.description')}
            action={inviteButton}
          />
          <Team />
        </section>
      </Layout>

      {isBusiness && (
        <TeamBusinessInviteDialog
          key={inviteSessionId}
          open={isInviteOpen}
          onOpenChange={(open?: boolean) => {
            setIsInviteOpen(open ?? false);
            if (!open) {
              setPrefillName(undefined);
              setPrefillEmail(undefined);
            }
          }}
          source={inviteSource}
          prefillName={prefillName}
          prefillEmail={prefillEmail}
        />
      )}

      {!isBusiness && (
        <TeamBusinessUpgradeDialog
          open={isInviteOpen}
          onOpenChange={(open?: boolean) => setIsInviteOpen(open ?? false)}
        />
      )}

      <TrialEndModal
        isOpen={isTrialEndOpen}
        onClose={() => {
          setIsTrialEndOpen(false);
          // Dismiss path: drop deep-link prefills so a later open via the
          // settings button doesn't inherit them. onSuccess intentionally
          // keeps them — it flows straight into the invite dialog.
          setPrefillName(undefined);
          setPrefillEmail(undefined);
        }}
        onSuccess={() => {
          setIsTrialEndOpen(false);
          openInviteDialog();
        }}
        origin="invite-member"
        title={t('team.invite.trialEndModal.title')}
        description={t('team.invite.trialEndModal.description')}
      />
    </>
  );
}
